/**
 * Pure helper for the Insights column-visibility preference. Kept free of
 * React and localStorage so the ordering + selection semantics can be
 * tested directly.
 *
 * `stored` is the persisted visible-order (or null if the user hasn't
 * interacted); `backendTypes` is the current set of metric types in
 * backend order. The returned object exposes:
 *
 *   - `visible`: the effective visible order for rendering. Derived from
 *     `stored` filtered to the current backend, or the first `defaultCount`
 *     backend types when `stored` is null.
 *   - `toggle(type)`: checkbox toggle. Removes `type` from visible or
 *     appends it. Returns the next stored value, or `null` if the change
 *     is rejected (unknown type, or would drop visible below `minCount`).
 *   - `reorder(active, over)`: drag-and-drop reorder. Dragging never
 *     unchecks an item. Rules:
 *       - visible → visible: reorder within visible (active takes over's slot).
 *       - visible → hidden:  clamp active to end of visible (stays selected).
 *       - hidden  → visible: promote active, insert at over's slot.
 *       - hidden  → hidden:  promote active, append to end of visible.
 *     Returns the next stored value, or `null` if the change is a no-op
 *     (active === over) or either type is unknown.
 */
export function columnVisibilityPreference(
  stored: readonly string[] | null,
  backendTypes: readonly string[],
  options: { defaultCount: number; minCount: number },
): {
  visible: string[]
  toggle: (type: string) => string[] | null
  reorder: (activeType: string, overType: string) => string[] | null
} {
  const backendSet = new Set(backendTypes)
  const visible =
    stored === null
      ? backendTypes.slice(0, options.defaultCount)
      : stored.filter((t) => backendSet.has(t))

  const visibleSet = new Set(visible)

  const toggle = (type: string): string[] | null => {
    if (!backendSet.has(type)) return null
    if (visibleSet.has(type)) {
      if (visible.length <= options.minCount) return null
      return visible.filter((t) => t !== type)
    }
    return [...visible, type]
  }

  const reorder = (activeType: string, overType: string): string[] | null => {
    if (activeType === overType) return null
    if (!backendSet.has(activeType) || !backendSet.has(overType)) return null

    const activeInVisible = visibleSet.has(activeType)
    const overInVisible = visibleSet.has(overType)

    if (activeInVisible && overInVisible) {
      const next = visible.filter((t) => t !== activeType)
      next.splice(next.indexOf(overType), 0, activeType)
      return next
    }
    if (activeInVisible) {
      // visible → hidden: clamp to end of visible.
      const next = visible.filter((t) => t !== activeType)
      next.push(activeType)
      return next
    }
    if (overInVisible) {
      // hidden → visible: promote, insert at over's slot.
      const next = [...visible]
      next.splice(next.indexOf(overType), 0, activeType)
      return next
    }
    // hidden → hidden: promote, append to end of visible.
    return [...visible, activeType]
  }

  return { visible: [...visible], toggle, reorder }
}
